import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import type { EmpresaRow } from '../types';

interface IaChatProps {
  password: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const QUICK_PROMPTS = [
  'Com quem devo falar hoje?',
  'Quem está sem resposta?',
  'Quais oportunidades acima de R$100 mil estão abertas?',
  'Quais clientes antigos devo reativar?',
  'Quem recebeu proposta e não fechou?',
  'Qual minha prioridade agora?',
];

const CANAIS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  { key: 'email', label: 'E-mail', icon: 'ri-mail-line' },
  { key: 'ligacao', label: 'Ligação', icon: 'ri-phone-line' },
];

const ACOES = [
  { key: 'proxima_acao', label: 'Próxima ação', icon: 'ri-compass-3-line' },
  { key: 'score', label: 'Score inteligente', icon: 'ri-fire-line' },
];

function fmtScoreClass(classificacao: string): string {
  if (classificacao === 'quente') return 'text-red-600';
  if (classificacao === 'prioritario') return 'text-orange-600';
  if (classificacao === 'acompanhar') return 'text-amber-600';
  return 'text-gray-500';
}

export default function IaChat({ password }: IaChatProps) {
  const [empresas, setEmpresas] = useState<EmpresaRow[]>([]);
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [empresasLoading, setEmpresasLoading] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);

  const [listening, setListening] = useState(false);
  const [voiceSupported] = useState(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    return !!SR;
  });
  const recognitionRef = useRef<any>(null);

  const loadEmpresas = async () => {
    setEmpresasLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'list_empresas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { empresas?: EmpresaRow[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEmpresas(parsed?.empresas ?? []);
    } catch {
      // silencioso, mantém lista vazia
    } finally {
      setEmpresasLoading(false);
    }
  };

  useEffect(() => {
    loadEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  const sendChat = async (text?: string) => {
    const question = (text ?? input).trim();
    if (!question || chatLoading) return;
    setInput('');
    setError(null);
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setChatLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-ai', {
        body: { action: 'chat', password, message: question },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { resposta?: string; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setMessages((m) => [...m, { role: 'assistant', content: parsed?.resposta ?? 'Sem resposta.' }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao consultar a IA.');
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ Não consegui processar agora. Tente novamente.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const sendVoice = async (text: string) => {
    setError(null);
    setMessages((m) => [...m, { role: 'user', content: `🎤 ${text}` }]);
    setChatLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-ai', {
        body: { action: 'voice_command', password, message: text, empresa_id: empresaId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as any;
      if (parsed?.error) throw new Error(parsed.error);

      let resposta = '';
      if (parsed.tipo === 'pergunta') {
        resposta = parsed.resposta ?? '';
      } else if (parsed.pendente) {
        resposta = parsed.mensagem ?? '';
      } else {
        const feitos = Array.isArray(parsed.realizadas) ? parsed.realizadas : [];
        const titulo = parsed.empresa_criada
          ? `✅ Empresa "${parsed.empresa}" criada e registrado:`
          : `✅ Registrado para "${parsed.empresa}":`;
        resposta = `${titulo}\n${feitos.map((f: string) => '• ' + f).join('\n')}`;
      }
      setMessages((m) => [...m, { role: 'assistant', content: resposta || 'Pronto.' }]);
      loadEmpresas();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao processar a fala.');
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ Não consegui processar. Tente novamente.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const startVoice = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      setError('Seu navegador não suporta voz. Use Chrome ou Edge.');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }

    const rec = new SR();
    rec.lang = 'pt-BR';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.continuous = false;

    rec.onstart = () => setListening(true);
    rec.onresult = (e: any) => {
      const text = e.results?.[0]?.[0]?.transcript ?? '';
      setListening(false);
      if (text.trim()) sendVoice(text.trim());
    };
    rec.onerror = () => {
      setListening(false);
      setError('Não consegui ouvir. Tente de novo.');
    };
    rec.onend = () => setListening(false);

    recognitionRef.current = rec;
    rec.start();
  };

  const runAction = async (action: string) => {
    if (!empresaId) {
      setError('Selecione uma empresa primeiro.');
      return;
    }
    setActionLoading(action);
    setError(null);
    setResult(null);
    try {
      let body: any = { action, password, empresa_id: empresaId };
      if (action === 'score') body.persist = true;

      const { data, error: invokeErr } = await supabase.functions.invoke('crm-ai', { body });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as any;
      if (parsed?.error) throw new Error(parsed.error);
      setResult({ ...parsed, action });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro na IA.');
    } finally {
      setActionLoading(null);
    }
  };

  const selectedEmpresa = empresas.find((e) => e.id === empresaId);

  const renderResult = () => {
    if (!result) return null;

    if (result.action === 'whatsapp') {
      return (
        <div className="space-y-3">
          <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl p-4">
            <p className="text-xs font-semibold text-[#128C7E] mb-1">Mensagem sugerida</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{result.texto ?? result.raw}</p>
          </div>
          {result.whatsapp_link ? (
            <a
              href={result.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#25D366] hover:bg-[#1EBE5D] px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-whatsapp-line"></i>
              Abrir WhatsApp
            </a>
          ) : (
            <p className="text-xs text-gray-400">Sem telefone cadastrado para abrir o WhatsApp direto.</p>
          )}
        </div>
      );
    }

    if (result.action === 'email') {
      return (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Assunto</p>
            <p className="text-sm font-semibold text-gray-900">{result.assunto}</p>
          </div>
          <div className="bg-gray-50 border border-hop-border rounded-xl p-4">
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{result.corpo ?? result.raw}</p>
          </div>
          <button
            onClick={() => navigator.clipboard?.writeText(`${result.assunto ?? ''}\n\n${result.corpo ?? result.raw ?? ''}`)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 bg-white border border-hop-border hover:bg-gray-50 px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-file-copy-line"></i>
            Copiar e-mail
          </button>
        </div>
      );
    }

    if (result.action === 'ligacao') {
      const roteiro = Array.isArray(result.roteiro) ? result.roteiro : [];
      return (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Objetivo</p>
            <p className="text-sm text-gray-900">{result.objetivo}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Roteiro</p>
            <ul className="space-y-1.5">
              {roteiro.map((item: string, i: number) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="w-5 h-5 shrink-0 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-full text-[11px] font-bold mt-0.5">
                    {i + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    if (result.action === 'proxima_acao') {
      return (
        <div className="space-y-3">
          <div className="bg-[#E8610A]/10 border border-[#E8610A]/20 rounded-xl p-4">
            <p className="text-xs font-semibold text-[#E8610A] mb-1">Próxima ação</p>
            <p className="text-sm font-semibold text-gray-900">{result.proxima_acao}</p>
            {result.prazo_dias != null && (
              <p className="text-xs text-gray-500 mt-1">
                <i className="ri-time-line mr-1"></i>
                em {result.prazo_dias} dia{result.prazo_dias === 1 ? '' : 's'}
              </p>
            )}
          </div>
          {result.motivo && <p className="text-sm text-gray-600">{result.motivo}</p>}
          {result.mensagem_pronta && (
            <div className="bg-gray-50 border border-hop-border rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 mb-1">Mensagem pronta</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{result.mensagem_pronta}</p>
            </div>
          )}
        </div>
      );
    }

    if (result.action === 'score') {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-4 bg-gray-50 border border-hop-border rounded-xl p-4">
            <div className="text-center">
              <p className={`text-4xl font-bold ${fmtScoreClass(result.classificacao)}`}>{result.score}</p>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Score</p>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 capitalize">{result.classificacao}</p>
              {result.justificativa && (
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">{result.justificativa}</p>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400">Score salvo na ficha da empresa.</p>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 border border-hop-border rounded-xl p-4">
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{result.raw ?? '—'}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
          <i className="ri-sparkling-2-line text-lg"></i>
        </div>
        <div>
          <h2 className="font-bold text-gray-900">Assistente de IA</h2>
          <p className="text-sm text-gray-500">O cérebro comercial que consulta seus dados reais</p>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
        {/* Chat */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-hop-border flex flex-col">
          <div className="p-4 border-b border-hop-border">
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => sendChat(p)}
                  disabled={chatLoading}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 border border-hop-border rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 p-4 space-y-4 min-h-[320px] max-h-[520px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-2xl mx-auto mb-3">
                  <i className="ri-chat-3-line text-2xl"></i>
                </div>
                <p className="text-sm text-gray-500">Pergunte qualquer coisa sobre seus leads, contatos e vendas.</p>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-[#E8610A] text-white rounded-br-sm'
                        : 'bg-gray-50 text-gray-800 border border-hop-border rounded-bl-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-50 border border-hop-border rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-[#E8610A]"></i>
                  <span className="text-sm text-gray-500">Pensando...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="p-4 border-t border-hop-border">
            {listening && (
              <div className="flex items-center gap-2 mb-2 px-1">
                <i className="ri-mic-fill text-red-600 animate-pulse"></i>
                <span className="text-sm text-gray-600">Ouvindo... pode falar</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
                placeholder="Pergunte à IA... (Enter para enviar)"
                className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white"
              />
              <button
                onClick={startVoice}
                disabled={chatLoading || !voiceSupported}
                title={voiceSupported ? 'Falar com a IA (ditar cadastro, atividade ou pergunta)' : 'Seu navegador não suporta voz. Use Chrome ou Edge.'}
                className={`inline-flex items-center justify-center w-11 h-11 rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                  listening
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <i className={`${listening ? 'ri-mic-fill' : 'ri-mic-line'} text-lg`}></i>
              </button>
              <button
                onClick={() => sendChat()}
                disabled={chatLoading || !input.trim()}
                className="inline-flex items-center justify-center w-11 h-11 bg-[#E8610A] text-white rounded-xl hover:bg-[#C4500A] cursor-pointer transition-colors disabled:opacity-50"
              >
                <i className="ri-send-plane-2-line text-lg"></i>
              </button>
            </div>
            {voiceSupported && (
              <p className="text-[11px] text-gray-400 mt-2 px-1">
                🎤 Fale para cadastrar um contato, registrar o que aconteceu ou agendar um lembrete. Ex.: "mandei e-mail pro Porto Digital e a Fulana me respondeu no WhatsApp".
              </p>
            )}
          </div>
        </div>

        {/* Ações rápidas */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-hop-border p-5">
          <h3 className="font-bold text-gray-900 mb-4">Preparar contato</h3>

          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            Empresa
          </label>
          <div className="relative mb-4">
            <select
              value={empresaId ?? ''}
              onChange={(e) => {
                setEmpresaId(e.target.value ? Number(e.target.value) : null);
                setResult(null);
              }}
              className="w-full appearance-none border border-gray-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white cursor-pointer"
            >
              <option value="">Selecione uma empresa...</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                  {e.cidade ? ` · ${e.cidade}` : ''}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
          </div>

          {empresasLoading && (
            <p className="text-xs text-gray-400 mb-4">Carregando empresas...</p>
          )}

          <div className="grid grid-cols-3 gap-2 mb-4">
            {CANAIS.map((c) => (
              <button
                key={c.key}
                onClick={() => runAction(c.key)}
                disabled={!empresaId || actionLoading != null}
                className="inline-flex flex-col items-center gap-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-hop-border rounded-xl px-2 py-3 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
              >
                <i className={`${c.icon} text-lg ${c.key === 'whatsapp' ? 'text-[#25D366]' : c.key === 'email' ? 'text-[#E8610A]' : 'text-gray-600'}`}></i>
                {actionLoading === c.key ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  c.label
                )}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {ACOES.map((a) => (
              <button
                key={a.key}
                onClick={() => runAction(a.key)}
                disabled={!empresaId || actionLoading != null}
                className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-hop-border rounded-xl px-3 py-3 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
              >
                <i className={`${a.icon} text-[#E8610A]`}></i>
                {actionLoading === a.key ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  a.label
                )}
              </button>
            ))}
          </div>

          {selectedEmpresa && (
            <div className="text-xs text-gray-500 mb-4 flex items-center gap-2">
              <i className="ri-information-line text-[#E8610A]"></i>
              <span>
                {selectedEmpresa.contatos?.length ?? 0} contato
                {(selectedEmpresa.contatos?.length ?? 0) === 1 ? '' : 's'} · etapa{' '}
                {selectedEmpresa.oportunidades?.[0]?.etapa ?? 'novo'}
              </span>
            </div>
          )}

          <div className="border-t border-hop-border pt-4">
            {result ? (
              renderResult()
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">
                Selecione uma empresa e escolha uma ação para gerar a mensagem, a próxima ação ou o score com IA.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}